/* eslint-disable max-lines */
import { createTheme } from '@material-ui/core/styles';
import { alpha, darken, lighten } from '@material-ui/core/styles/colorManipulator';
import createPalette from '@material-ui/core/styles/createPalette';
import muiShadows from '@material-ui/core/styles/shadows';
export const shadows = [...muiShadows];
const fallbackFonts = [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    '"Helvetica"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
];
export const palette = createPalette({
    type: 'light',
    text: {
        primary: 'rgba(33, 31, 31, 0.87)',
        secondary: 'rgba(33, 31, 31, 0.64)',
        disabled: 'rgba(33, 31, 31, 0.38)',
        inverse: '#FFFFFF',
        // hint: '',
    },
    primary: {
        main: '#06a76c',
        dark: '#058556',
        light: '#07c57f',
        contrastText: '#052E22',
        lightShade190p: lighten('#06a76c', 0.9),
        darkShade160p: darken('#06a76c', 0.6),
    },
    secondary: {
        main: '#2C324F',
        dark: '#040A27',
        light: '#565B7B',
        contrastText: '#FFFFFF',
        lightShade190p: lighten('#2C324F', 0.9),
        darkShade160p: darken('#2C324F', 0.6),
    },
    error: {
        main: '#E14F42',
        dark: '#CF2F21',
        light: '#E8786E',
        contrastText: 'rgba(57, 57, 57, 0.23)',
        lightShade190p: lighten('#E14F42', 0.9),
        darkShade160p: darken('#E14F42', 0.6),
    },
    warning: {
        main: '#F2C113',
        dark: '#BB9100',
        light: '#FFF354',
        contrastText: '#3D2F05',
        lightShade190p: lighten('#F2C113', 0.9),
        darkShade160p: darken('#F2C113', 0.6),
    },
    info: {
        main: '#369DD9',
        dark: '#006FA7',
        light: '#74CEFF',
        contrastText: '#0A2B3D',
        lightShade190p: lighten('#369DD9', 0.9),
        darkShade160p: darken('#369DD9', 0.6),
    },
    success: {
        main: '#06A76C',
        dark: '#058556',
        light: '#07C57F',
        contrastText: '#052E22',
        lightShade190p: lighten('#06A76C', 0.9),
        darkShade160p: darken('#06a76c', 0.6),
    },
    action: {
        active: 'rgba(0, 0, 0, 0.54)',
        hover: 'rgba(0, 0, 0, 0.04)',
        selected: 'rgba(0, 0, 0, 0.04)',
        disabled: 'rgba(0, 0, 0, 0.26)',
        disabledBackground: 'rgba(0, 0, 0, 0.12)',
        focus: 'rgba(0, 0, 0, 0.12)',
        disabledOpacity: 0.38,
    },
    background: {
        default: 'rgba(249, 249, 249, 1)',
        canvas: '',
        paper: 'rgba(255, 255, 255, 1)',
        hint: '#393939',
    },
    divider: 'rgba(57, 57, 57, 0.12)',
    common: {
        black: '#000000',
        white: '#FFFFFF',
    },
    grey: {
        50: '#F6F5F4',
        100: '#EAE8E6',
        200: '#D5D0CC',
        300: '#E0E0E0',
        500: '#949494',
        700: '#616161',
    },
    deepGrey: {
        100: 'rgba(155, 155, 155, 0.45)',
        200: 'rgba(57, 57, 57, 0.42)',
        300: '#979797',
    },
    tooltip: {
        darkTooltip: '#393939',
        lightTooltip: '#747474',
    },
});
/**
 * Liquidity Theme
 * This is the base theme for Liquidity. It defines the color scheme and typography that material-ui will use
 */
export const LiquidityTheme = createTheme({
    name: 'liquidity',
    typography: {
        h1: {
            fontFamily: ['Libre Baskerville', 'serif', ...fallbackFonts].join(','),
            fontWeight: 400,
            fontSize: '6rem',
        },
    },
    overrides: {
        MuiLink: {
            root: {
                fontWeight: 700,
            },
        },
        MuiCssBaseline: {
            /*
              it removes auto clear icon from search box
             */
            '@global': {
                html: {
                    fontFamily: ['Roboto', ...fallbackFonts].join(', '),
                },
                'input[type="search"]::-webkit-search-cancel-button': {
                    '-webkit-appearance': 'none',
                },
            },
        },
        MuiButton: {
            root: {
                textTransform: 'capitalize',
                borderRadius: '3rem',
                fontWeight: 700,
                border: '1px solid transparent',
                paddingY: 2,
                paddingX: 3,
            },
            text: {
                padding: '6px 16px',
            },
            outlined: {
                borderWidth: '2px',
                '&$disabled, &:hover': {
                    borderWidth: 'wpx',
                },
            },
            sizeLarge: {
                fontSize: '1.1875rem',
                fontWeight: 700,
            },
        },
        DestructiveButton: {
            root: {
                '&:disabled': {
                    color: alpha(palette.text.inverse, palette.action.disabledOpacity),
                    backgroundColor: alpha(palette.error.main, palette.action.disabledOpacity),
                },
            },
        },
        CTAButton: {
            root: {
                backgroundColor: palette.primary.main,
                // border: '3px solid transparent',
                border: 0,
                color: palette.text.inverse,
                position: 'relative',
                transformStyle: 'preserve-3d',
                transition: 'all 150ms cubic-bezier(.175, .885, .32, 1.275)',
                '&::before': {
                    content: '" "',
                    background: palette.secondary.main,
                    borderRadius: 'inherit',
                    height: '100%',
                    width: '100%',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    position: 'absolute',
                    transform: 'translate3d(-.2em, .3em, -1em)',
                    transition: 'all 150ms cubic-bezier(.175, .885, .32, 1.275)',
                },
                '&:hover': {
                    backgroundColor: palette.primary.dark,
                    boxShadow: shadows[0],
                    '&::before': {
                        boxShadow: shadows[2],
                    },
                },
                '&:active': {
                    transform: 'translate(-.2em, .3em)',
                    '&::before': {
                        transform: 'translate3d(0, 0, -1em)',
                    },
                },
                '&:disabled': {
                    backgroundColor: lighten(palette.primary.main, 0.3),
                    color: palette.text.inverse,
                    '&::before': {
                        backgroundColor: lighten(palette.secondary.main, 0.3),
                    },
                },
            },
            containedPrimary: {
                backgroundColor: palette.secondary.main,
                '&:hover': {
                    backgroundColor: palette.secondary.dark,
                },
                '&$disabled': {
                    backgroundColor: lighten(palette.secondary.main, 0.3),
                    '&::before': {
                        backgroundColor: darken(palette.common.white, 0.3),
                    },
                },
                '&::before': {
                    background: palette.common.white,
                },
            },
            containedSecondary: {
                backgroundColor: palette.primary.main,
                '&$disabled': {
                    backgroundColor: lighten(palette.primary.main, 0.3),
                    '&::before': {
                        backgroundColor: darken(palette.common.white, 0.3),
                    },
                },
                '&::before': {
                    background: palette.common.white,
                },
            },
        },
        MuiTable: {
            root: {
                borderCollapse: 'collapse',
                borderSpacing: 0,
                backgroundColor: palette.common.white,
            },
        },
        MuiTableRow: {
            root: {
                borderBottom: `1px solid ${palette.divider}`,
                '&$hover:hover': {
                    backgroundColor: palette.action.hover,
                },
                '&$selected': {
                    backgroundColor: alpha(palette.primary.main, 0.04),
                },
                '&$selected:hover': {
                    backgroundColor: alpha(palette.success.main, 0.04),
                },
            },
        },
        MuiTableCell: {
            root: {
                borderBottom: 'none',
                paddingX: 8,
                paddingY: 16,
                '&:first-child': {
                    paddingLeft: 16,
                },
                '&:last-child': {
                    paddingRight: 16,
                },
            },
        },
        MuiTooltip: {
            tooltip: {
                fontSize: '0.8125rem',
                fontWeight: 500,
                padding: '4px 8px',
                borderRadius: 5,
            },
        },
        MuiCheckbox: {
            root: {
                color: palette.deepGrey[200],
                '&$disabled': {
                    color: palette.action.disabled,
                },
                '&$checked': {
                    color: palette.grey[700],
                    '&$disabled': {
                        color: palette.action.disabled,
                    },
                },
            },
            colorPrimary: {
                '&$disabled': {
                    color: palette.action.disabled,
                },
                '&$checked': {
                    color: palette.primary.main,
                    '&$disabled': {
                        color: alpha(palette.primary.main, palette.action.disabledOpacity),
                    },
                },
            },
            colorSecondary: {
                '&$disabled': {
                    color: palette.action.disabled,
                },
                '&$checked': {
                    color: palette.secondary.main,
                    '&$disabled': {
                        color: alpha(palette.secondary.main, palette.action.disabledOpacity),
                    },
                },
            },
        },
        MuiRating: {
            root: {
                color: palette.warning.main,
            },
        },
        MuiChip: {
            root: {
                fontWeight: 600,
                fontSize: '0.875rem',
            },
            outlined: {
                backgroundColor: alpha(palette.text.disabled, 0.05),
            },
            outlinedPrimary: {
                backgroundColor: alpha(palette.primary.main, 0.05),
            },
            outlinedSecondary: {
                backgroundColor: alpha(palette.secondary.main, 0.05),
            },
        },
        MuiTextField: {
            root: {
                '& $label': {
                    '&.Mui-error': {
                        color: palette.error.dark,
                    },
                    '&.Mui-focused': {
                        color: palette.success.dark,
                    },
                },
                '& p': {
                    '&.Mui-error': {
                        color: palette.error.dark,
                    },
                    '&.Mui-focused': {
                        color: palette.success.dark,
                    },
                },
            },
        },
        AlertBanner: {
            filledSuccess: {
                color: palette.text.primary,
            },
            filledInfo: {
                color: palette.text.primary,
            },
            filledWarning: {
                color: palette.text.primary,
            },
            filledError: {
                color: palette.text.primary,
            },
            standardSuccess: {
                color: darken(palette.success.main, 0.6),
            },
            standardInfo: {
                color: darken(palette.info.main, 0.6),
            },
            standardWarning: {
                color: darken(palette.warning.main, 0.6),
            },
            standardError: {
                color: darken(palette.error.main, 0.6),
            },
        },
    },
    palette,
    props: {
        MuiButton: {
            disableElevation: true,
        },
        MuiCheckbox: {
            color: 'default',
        },
    },
    spacing: 8,
    shape: {
        borderRadius: 5,
    },
    shadows,
});
//# sourceMappingURL=Liquidity.js.map